import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Users, Compass, ShieldCheck } from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";
import { ABOUT } from "@/constants/testIds";

const About = () => {
    return (
        <div data-testid={ABOUT.page}>
            {/* Page header */}
            <section className="relative bg-indigo_brand text-white overflow-hidden">
                <div className="ih-container py-20 md:py-28">
                    <p
                        className="ih-eyebrow"
                        style={{ color: "#C9A646" }}
                    >
                        About The Intuitus Hub
                    </p>
                    <h1 className="mt-3 font-montserrat font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">
                        Helping organizations adopt AI
                        <span className="block text-gold_brand">
                            without losing what makes them human.
                        </span>
                    </h1>
                    <p className="mt-7 font-opensans text-white/80 text-lg leading-relaxed max-w-2xl">
                        The Intuitus Hub™ is a research-informed consulting and
                        professional development organization for executives,
                        leaders, and teams navigating an AI-enabled world.
                    </p>
                </div>
                <div className="absolute -bottom-px left-0 right-0 h-px bg-gold_brand/40" />
            </section>

            {/* Mission */}
            <section className="ih-section bg-white">
                <div className="ih-container grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:items-start">
                    <div>
                        <SectionHeading
                            eyebrow="Our Mission"
                            title="Strengthen the human capabilities AI cannot replace."
                            kicker="We exist to make sure organizations adopting AI also invest in the awareness, empathy, adaptability, and leadership that no model can deliver. Technology amplifies whatever it touches — we help leaders make sure what gets amplified is worth keeping."
                        />
                    </div>
                    <div className="relative rounded-3xl overflow-hidden shadow-medium aspect-[4/3]">
                        <img
                            src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=1200&q=80"
                            alt="Executive learning session"
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                        />
                    </div>
                </div>
            </section>

            {/* Approach */}
            <section className="ih-section bg-surface">
                <div className="ih-container">
                    <SectionHeading
                        eyebrow="Our Approach"
                        title="Research-informed. Executive-ready. Human-centered."
                        kicker="Every engagement is grounded in research from psychology, organizational development, and responsible AI adoption — then translated into the practical decisions executives actually have to make."
                    />

                    <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                        {[
                            {
                                icon: BookOpen,
                                title: "Research-Informed",
                                body: "Drawn from established work in cognitive science, leadership, and organizational behavior — not opinions.",
                            },
                            {
                                icon: Users,
                                title: "Human-Centered",
                                body: "We design for people first. AI is a tool; judgment, empathy, and meaning stay with humans.",
                            },
                            {
                                icon: Compass,
                                title: "Executive-Ready",
                                body: "Built for CEOs, HR leaders, and senior teams who need to act, not just discuss.",
                            },
                            {
                                icon: ShieldCheck,
                                title: "Responsibly AI-Enabled",
                                body: "Adoption with oversight, accountability, and a clear line on what humans must own.",
                            },
                        ].map(({ icon: Icon, title, body }) => (
                            <div key={title} className="ih-card p-7">
                                <span className="inline-flex w-11 h-11 items-center justify-center rounded-xl bg-indigo_brand/8">
                                    <Icon size={20} className="text-indigo_brand" />
                                </span>
                                <h3 className="mt-5 font-montserrat font-bold text-indigo_brand text-lg">
                                    {title}
                                </h3>
                                <p className="mt-3 font-opensans text-sm text-[#4A5568] leading-relaxed">
                                    {body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Who we serve */}
            <section className="ih-section bg-white">
                <div className="ih-container grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:items-center">
                    <div>
                        <SectionHeading
                            eyebrow="Who We Serve"
                            title="Executives and organizations preparing for an AI-enabled future."
                            kicker="The Intuitus Hub™ partners with leadership teams across industries to assess readiness, build human capability, and integrate AI responsibly."
                        />
                        <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                            {[
                                "Small & Mid-Sized Business Owners",
                                "CEOs & Executive Teams",
                                "Hospital Administrators",
                                "University Presidents",
                                "Agency & Call-Center Directors",
                                "HR & L&D Leaders",
                                "Organizational Development Professionals",
                                "Consultants & Advisors",
                            ].map((aud) => (
                                <li
                                    key={aud}
                                    className="flex items-start gap-2.5 font-opensans text-sm text-[#4A5568]"
                                >
                                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-green_brand shrink-0" />
                                    {aud}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="relative">
                        <blockquote className="ih-quote text-xl sm:text-2xl leading-snug border-l-4 border-green_brand pl-6">
                            We don&rsquo;t help organizations become more like AI.
                            <br />
                            We help them stay unmistakably human while using it
                            wisely.
                        </blockquote>
                        <p className="mt-5 font-poppins text-xs uppercase tracking-[0.18em] text-[#4A5568]">
                            — The Intuitus Hub™
                        </p>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="ih-section bg-surface">
                <div className="ih-container">
                    <div className="ih-card p-10 md:p-14 grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center shadow-medium">
                        <div>
                            <p className="ih-eyebrow">Start the Conversation</p>
                            <h2 className="mt-3 font-montserrat font-bold text-indigo_brand text-3xl sm:text-4xl leading-tight">
                                See where your organization stands.
                            </h2>
                            <p className="mt-5 font-opensans text-[#4A5568] text-base max-w-xl leading-relaxed">
                                Take the HUMAN+ Snapshot™ to get a personalized
                                Executive Report — your six-pillar scorecard and
                                a clear next step.
                            </p>
                        </div>
                        <div className="flex flex-col gap-3 sm:flex-row lg:flex-col lg:items-end">
                            <Link
                                to="/snapshot"
                                data-testid={ABOUT.missionCta}
                                className="ih-btn-primary"
                            >
                                Take the Snapshot
                                <ArrowRight size={16} />
                            </Link>
                            <Link to="/contact" className="ih-btn-secondary">
                                Contact Us
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default About;
